import queryString from 'query-string';
import {
  HttpMethod,
  IHttpResponse
} from '@aph/common-react';

import {
  IPublicationSearchRequest,
  IPublicationSearchResponse,
  IBillsDigestSearchRequest,
  IBillsDigestSearchResponse,
  IGetParliamentsResponse,
  IStatisticsSearchRequest,
  IStatisticsSearchResponse,
  IGetLookupResponse,
  ILibrarySearchResponse,
  ILectureSearchRequest,
  IGetLectureDetailsResponse
} from 'services';
import { executeFetchService } from 'global.helpers';

// Setup the querystring options
const options: queryString.StringifyOptions = {
  encode: false,
  arrayFormat: 'comma'
};

/**
 * IUseSearchService
 *
 * @description Returns object with library search services
 */
export interface IUseSearchService {
  getSearchCategories: () => Promise<IHttpResponse<IGetLookupResponse>>;
  getSearchPublicationTypes: () => Promise<IHttpResponse<IGetLookupResponse>>;
  getSearchPortfolios: () => Promise<IHttpResponse<IGetLookupResponse>>;
  getParliaments: () => Promise<IHttpResponse<IGetParliamentsResponse>>;
  searchPublications: (request: IPublicationSearchRequest) => Promise<IHttpResponse<IPublicationSearchResponse>>;
  searchBillsDigest: (request: IBillsDigestSearchRequest) => Promise<IHttpResponse<IBillsDigestSearchResponse>>;
  searchDisallowableInstruments: (request: IBillsDigestSearchRequest) => Promise<IHttpResponse<IBillsDigestSearchResponse>>;
  getStatisticsSearchTypes: () => Promise<IHttpResponse<IGetLookupResponse>>;
  getStatisticsSearchCategories: () => Promise<IHttpResponse<IGetLookupResponse>>;
  searchStatistics: (request: IStatisticsSearchRequest) => Promise<IHttpResponse<IStatisticsSearchResponse>>;
  getSearchLectureTopics: () => Promise<IHttpResponse<IGetLookupResponse>>;
  searchLectures: (request: ILectureSearchRequest) => Promise<IHttpResponse<ILibrarySearchResponse>>;
  getLectureDetails: (lectureId: string) => Promise<IHttpResponse<IGetLectureDetailsResponse>>;
}

/**
 * UseSearchService
 *
 * @description Access all search services
 */
const useSearchService = (baseUrl: string): IUseSearchService => {
  /**
   * GetSearchCategories
   *
   * @description Get list of all categories that can be used to search on
   */
  const getSearchCategories = () => {
    return executeFetchService<IGetLookupResponse>(`${baseUrl}/content/library/categories`, HttpMethod.GET);
  };

  /**
   * GetSearchTypes
   *
   * @description Get list of all types that can be used to search on
   */
  const getSearchPublicationTypes = () => {
    return executeFetchService<IGetLookupResponse>(`${baseUrl}/content/library/types`, HttpMethod.GET);
  };

  /**
   * GetSearchTypes
   *
   * @description Get list of all types that can be used to search on
   */
  const getSearchPortfolios = () => {
    return executeFetchService<IGetLookupResponse>(`${baseUrl}/content/library/portfolios`, HttpMethod.GET);
  };

  /**
   * GetParliaments
   *
   * @description Get list of parliaments
   */
  const getParliaments = () => {
    return executeFetchService<IGetParliamentsResponse>(`${baseUrl}/content/parliaments`, HttpMethod.GET);
  };

  /**
   * SearchPublications
   *
   * @description Get list of all published documents
   * @param {IPublicationSearchRequest} request Request model
   */
  const searchPublications = (request: IPublicationSearchRequest) => {
    return executeFetchService<IPublicationSearchResponse>(`${baseUrl}/search/library/publications?${queryString.stringify(request, options)}`, HttpMethod.GET);
  };

  /**
   * SearchBillsDigest
   *
   * @description Get list of all Bills Digest
   * @param {IBillsDigestSearchRequest} request Request model
   */
  const searchBillsDigest = (request: IBillsDigestSearchRequest) => {
    return executeFetchService<IBillsDigestSearchResponse>(`${baseUrl}/search/library/billsdigests?${queryString.stringify(request, options)}`, HttpMethod.GET);
  };

  /**
   * SearchDisallowableInstruments
   *
   * @description Get list of all disallowable instruments
   * @param {IBillsDigestSearchRequest} request Request model
   */
  const searchDisallowableInstruments = (request: IBillsDigestSearchRequest) => {
    return executeFetchService<IBillsDigestSearchResponse>(`${baseUrl}/search/library/disallowableinstrumentupdates?${queryString.stringify(request, options)}`, HttpMethod.GET);
  };

  /**
   * getStatisticsSearchTypes
   *
   * @description Get list of all statistics types that can be used to search on
   */
  const getStatisticsSearchTypes = () => {
    return executeFetchService<IGetLookupResponse>(`${baseUrl}/content/library/statisticsTypes`, HttpMethod.GET);
  };

  /**
   * getStatisticSearchCategories
   *
   * @description Get list of all statistic categories that can be used to search on
   */
  const getStatisticsSearchCategories = () => {
    return executeFetchService<IGetLookupResponse>(`${baseUrl}/content/library/statisticsCategories`, HttpMethod.GET);
  };

  /**
   * SearchStatistics
   *
   * @description Get list of all published documents
   * @param {IPublicationSearchRequest} request Request model
   */
  const searchStatistics = (request: IStatisticsSearchRequest) => {
    return executeFetchService<IStatisticsSearchResponse>(`${baseUrl}/search/library/statistics?${queryString.stringify(request, options)}`, HttpMethod.GET);
  };

  /**
   * SearchLectureTopics
   *
   * @description Get list of lecture topic or subtopics
   */
  const getSearchLectureTopics = () => {
    return executeFetchService<IGetLookupResponse>(`${baseUrl}/content/library/lectureTopics`, HttpMethod.GET);
  };

  /**
   * GetSearchLectures
   *
   * @description Get filtered list of lectures from sitecore
   * @param {ILectureSearchRequest} request Request model
   */
  const searchLectures = (request: ILectureSearchRequest) => {
    return executeFetchService<ILibrarySearchResponse>(`${baseUrl}/search/library/lectures?${queryString.stringify(request, options)}`, HttpMethod.GET);
  };

  /**
   * GetLectureDetails
   *
   * @description Service to retrieve lecture details
   * @param {string} lectureId Lecture ID
   */
  const getLectureDetails = (lectureId: string) => {
    return executeFetchService<IGetLectureDetailsResponse>(`${baseUrl}/search/library/lectures/${lectureId}`, HttpMethod.GET);
  };

  return {
    getSearchCategories,
    getSearchPublicationTypes,
    getSearchPortfolios,
    getParliaments,
    searchPublications,
    searchBillsDigest,
    searchDisallowableInstruments,
    getStatisticsSearchTypes,
    getStatisticsSearchCategories,
    searchStatistics,
    getSearchLectureTopics,
    searchLectures,
    getLectureDetails
  };
};

export default useSearchService;